//add-crop.tsx

import Button from "@mui/material/Button";
import { useState, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { cropsApi } from "../services/api/crops";
import { Box } from "@mui/material";
import LoadingBackdrop from "./loading-backdrop";
import CropForm, {
  CropFormValues,
  convertToUnitSystem,
  otherUnitSystem,
} from "./crop-form";
import Page from "./page";
import { ErrorContext, UserContext } from "./app-routes";
import { Seed } from "../models/seed";
import { seedsApi } from "../services/api/seeds";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import { Crop } from "../models/crop";
import type { FormikHelpers } from "formik";
import StickyActionBar from "./sticky-action-bar";
import { Tray } from "../models/tray";
import { traysApi } from "../services/api/trays";
import { Grower } from "../models/grower";
import { getCustomLabel } from "../utils/custom-labels";

export default function AddCrop() {
  const history = useHistory();
  const formId = "add-crop-form";
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [wasSaved, setWasSaved] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [seeds, setSeeds] = useState<Seed[] | undefined>(undefined);
  const [trays, setTrays] = useState<Tray[] | null>(null);
  const { state } = useLocation<{ crop: Crop }>();
  const context = useContext(UserContext);
  const grower = context.grower;

  if (!grower) {
    throw new Error("Invalid context");
  }

  const isPro = grower.Plan === "pro";

  const initialValues: CropFormValues = state
    ? {
        id: 0,
        name: state.crop.name,
        shortCode: state.crop.shortCode,
        seedId: state.crop.seedId,
        trayId: "",
        seedingRate: state.crop.seedingRate.toString(),
        seedingRateOther: convertToUnitSystem(
          otherUnitSystem(grower.UnitSystem),
          state.crop.seedingRate,
        ).toFixed(2),
        overnightSoak: state.crop.overnightSoak,
        soakTimeMinutes: state.crop.soakTimeMinutes.toString(),
        germinationDays: state.crop.germinationDays.toString(),
        blackoutDays: state.crop.blackoutDays.toString(),
        daysToMaturity: state.crop.daysToMaturity.toString(),
        cutYield: state.crop.cutYield?.toString(),
        notes: state.crop.notes,
        archived: !!state.crop.archivedAt,
        bufferPercentage: state.crop.bufferPercentage.toString(),
      }
    : {
        id: 0,
        name: "",
        shortCode: "",
        seedId: "",
        trayId: "",
        seedingRate: "",
        seedingRateOther: "",
        overnightSoak: false,
        soakTimeMinutes: "",
        germinationDays: "",
        blackoutDays: "",
        daysToMaturity: "",
        cutYield: "",
        notes: "",
        archived: false,
        bufferPercentage: "0",
      };

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (!isMounted) return;
      try {
        setBusy(true);
        const seeds = await seedsApi.getAll();
        if (isMounted) {
          setSeeds(seeds);
        }
        const trays = isPro ? await traysApi.getAll() : [];
        setTrays(trays);
      } catch (error) {
        if (isMounted) {
          handleError(error);
        }
      } finally {
        if (isMounted) {
          setBusy(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [handleError]);

  const handleAddCrop = async (
    values: CropFormValues,
    helpers: FormikHelpers<CropFormValues>,
  ) => {
    setBusy(true);

    try {
      await cropsApi.add({
        id: 0,
        name: values.name,
        shortCode: values.shortCode,
        seedId: values.seedId === "" ? 0 : Number(values.seedId),
        trayId: values.trayId === "" ? null : Number(values.trayId),
        seedingRate: parseFloat(values.seedingRate),
        overnightSoak: values.overnightSoak,
        soakTimeMinutes: parseInt(values.soakTimeMinutes),
        germinationDays: parseInt(values.germinationDays),
        blackoutDays: parseInt(values.blackoutDays),
        daysToMaturity: parseInt(values.daysToMaturity),
        cutYield: values.cutYield ? parseFloat(values.cutYield) : undefined,
        notes: values.notes,
        archivedAt: values.archived ? DateTime.now().toISO() : null,
        bufferPercentage: parseFloat(values.bufferPercentage),
      });

      mixpanel.track("Add Crop");

      setWasSaved(true); // 🔥 important
      helpers.setSubmitting(false);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        helpers.setFieldError("shortCode", "This short code is already in use");
        return;
      }
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (wasSaved) {
      history.replace("/home/crops");
    }
  }, [wasSaved, history]);

  return (
    <Page title={`Add ${getCustomLabel(grower ?? null, "crops")}`}>
      {seeds && trays && (
        <>
          <CropForm
            formId={formId}
            initialValues={initialValues}
            seeds={seeds}
            trays={trays}
            onSubmit={handleAddCrop}
            saved={wasSaved}
            grower={grower}
          />

          <StickyActionBar position="bottom">
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => goBackOrTo(history, "/home/crops")}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              type="submit"
              form={formId}
              disabled={busy}
            >
              Add
            </Button>
          </StickyActionBar>

          {/* optional spacer so the last form fields don’t hide behind the sticky bar */}
          <Box sx={{ height: 72 }} />
        </>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
